import React, { useState } from 'react';
import {
  useListFirmsQuery,
  useAddFirmMutation,
  useDeleteFirmMutation,
} from '../app/api';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
};

const inputStyle = {
  width: '100%', padding: '6px 10px', border: `1px solid ${brand.softGray}`,
  borderRadius: 6, fontSize: '0.85rem', color: brand.deepBlue, outline: 'none', background: 'white',
};

// Shared firms manager — the firms list is a single lookup used across the app,
// editable from both the HR Config and Tech Integration pages. Add/delete are
// gated to HR or TECH on the server.
export default function FirmsSection() {
  const { data: firms = [], isLoading, isError } = useListFirmsQuery();
  const [addFirm, addState] = useAddFirmMutation();
  const [deleteFirm] = useDeleteFirmMutation();

  const [newName, setNewName] = useState('');
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    const name = newName.trim();
    if (!name) return;
    try {
      await addFirm({ name }).unwrap();
      setNewName('');
    } catch (err) {
      setError(err?.data?.detail || 'Could not add firm.');
    }
  };

  const remove = async (firm) => {
    if (!window.confirm(`Remove firm "${firm.name}"? Existing employees assigned to this firm will keep their value until you edit them.`)) return;
    try {
      await deleteFirm({ id: firm.id }).unwrap();
    } catch (err) {
      window.alert(err?.data?.detail || 'Delete failed.');
    }
  };

  return (
    <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 12px rgba(10,41,71,0.08)', overflow: 'hidden', marginBottom: '1.5rem' }}>
      <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.875rem 1.25rem' }}>
        <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>
          <i className="bi bi-building me-2" />Firms
        </h5>
      </div>

      <form onSubmit={submit} style={{ padding: '1rem 1.25rem', borderBottom: `1px solid ${brand.softGray}`, display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          value={newName}
          onChange={e => setNewName(e.target.value)}
          placeholder="New firm name"
          style={{ ...inputStyle, maxWidth: 320 }}
        />
        <button
          type="submit"
          disabled={addState.isLoading || !newName.trim()}
          style={{
            background: brand.deepBlue, color: 'white', border: 'none', borderRadius: 6,
            padding: '6px 14px', fontWeight: 600, fontSize: '0.85rem',
            opacity: (addState.isLoading || !newName.trim()) ? 0.7 : 1,
          }}
        >
          <i className="bi bi-plus-lg me-1" />Add Firm
        </button>
        {error && (
          <span style={{ color: '#b00020', fontSize: '0.85rem' }}>
            <i className="bi bi-exclamation-triangle me-1" />{error}
          </span>
        )}
      </form>

      {isLoading ? (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <div className="spinner-border" style={{ color: brand.tealBlue }} />
        </div>
      ) : isError ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: brand.charcoal }}>
          Unable to load firms.
        </div>
      ) : firms.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: brand.charcoal }}>
          No firms yet — add one above.
        </div>
      ) : (
        <table className="table table-hover mb-0" style={{ fontSize: '0.875rem' }}>
          <thead style={{ background: brand.softGray }}>
            <tr>
              <th style={{ padding: '0.6rem 1.25rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Firm</th>
              <th style={{ padding: '0.6rem 1.25rem', color: brand.deepBlue, fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {firms.map(f => (
              <tr key={f.id}>
                <td style={{ padding: '0.6rem 1.25rem', verticalAlign: 'middle', fontWeight: 700, color: brand.deepBlue }}>
                  {f.name}
                </td>
                <td style={{ padding: '0.6rem 1.25rem', verticalAlign: 'middle', textAlign: 'right' }}>
                  <button
                    onClick={() => remove(f)}
                    style={{ background: 'white', border: `1px solid ${brand.softGray}`, color: '#b00020', padding: '4px 10px', borderRadius: 6, fontSize: '0.78rem' }}
                  >
                    <i className="bi bi-trash me-1" />Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
